import React, { useEffect, useState } from "react";
import useFetch from "hooks/useFetch";
import SectionTabs from "pages/AdminLayout/SectionTabs";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Icon,
  IconButton,
  PageHeader,
  Skeleton,
  styles as ui,
  TextField,
  useConfirm,
  useToast,
} from "stories/Admin";
import ContentModal from "../DuaPage/ContentModal";
import styles from "./ExpressionsPage.module.scss";

interface Expression {
  id: number;
  arabic: string;
  translit: string;
  translation: string;
  situation: string;
}

type ExpressionFields = Omit<Expression, "id">;

const ExpressionFieldsForm: React.FC<{
  value: ExpressionFields;
  onChange: (value: ExpressionFields) => void;
}> = ({ value, onChange }) => (
  <>
    <TextField
      label="Arabisk"
      lang="ar"
      dir="rtl"
      className={styles.arabicInput}
      value={value.arabic}
      onChange={(e) => onChange({ ...value, arabic: e.target.value })}
    />
    <TextField
      label="Translitterasjon"
      value={value.translit}
      onChange={(e) => onChange({ ...value, translit: e.target.value })}
    />
    <TextField
      label="Oversettelse"
      value={value.translation}
      onChange={(e) => onChange({ ...value, translation: e.target.value })}
    />
    <TextField
      label="Situasjon"
      help="Når uttrykket brukes, f.eks. «Når man nyser»."
      value={value.situation}
      onChange={(e) => onChange({ ...value, situation: e.target.value })}
    />
  </>
);

const ExpressionsList: React.FC = () => {
  const { execute, isLoading, error } = useFetch();
  const toast = useToast();
  const confirm = useConfirm();
  const [expressions, setExpressions] = useState<Expression[]>([]);
  const [selectedExpression, setSelectedExpression] = useState<Omit<
    Expression,
    "id"
  > | null>(null);

  const [newExpression, setNewExpression] = useState<Omit<Expression, "id">>({
    arabic: "",
    translit: "",
    translation: "",
    situation: "",
  });
  const [selectedExpressionId, setSelectedExpressionId] = useState<
    number | null
  >(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const fetchExpressions = async () => {
      const response = await execute("/api/v1/expressions", "GET");
      if (response) {
        setExpressions(response as Expression[]);
      }
      setLoaded(true);
    };
    fetchExpressions();
  }, []);

  const handleEditClick = (expression: Expression) => {
    const { id, ...editableFields } = expression;
    setSelectedExpression(editableFields);
    setSelectedExpressionId(id);
    setShowEditModal(true);
  };

  const handleSave = async () => {
    if (!selectedExpression || selectedExpressionId === null) return;

    setIsSubmitting(true);
    try {
      const payload = { ...selectedExpression };

      const response = await execute(
        `/api/v1/expressions/${selectedExpressionId}`,
        "PATCH",
        payload,
      );

      if (response) {
        setExpressions((prevExpressions) =>
          prevExpressions.map((exp) =>
            exp.id === selectedExpressionId
              ? { ...exp, ...selectedExpression }
              : exp,
          ),
        );
        setShowEditModal(false);
        toast.success("Uttrykket er lagret.");
      } else {
        toast.error("Kunne ikke lagre uttrykket. Prøv igjen.");
      }
    } catch (err) {
      console.error("Error updating expression:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddNew = async () => {
    setIsSubmitting(true);
    try {
      const response = await execute(
        "/api/v1/expressions",
        "POST",
        newExpression,
      );

      if (response) {
        setExpressions((prevExpressions) => [
          ...prevExpressions,
          response as Expression,
        ]);
        setShowAddModal(false);
        setNewExpression({
          arabic: "",
          translit: "",
          translation: "",
          situation: "",
        });
        toast.success("Uttrykket er lagt til.");
      } else {
        toast.error("Kunne ikke legge til uttrykket. Prøv igjen.");
      }
    } catch (err) {
      console.error("Feil ved oppretting av nytt uttrykk:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = async (expression: Expression) => {
    const ok = await confirm({
      title: "Slette uttrykket?",
      message: (
        <>
          <b>{expression.translit || expression.arabic}</b> fjernes fra appen.
          Dette kan ikke angres.
        </>
      ),
      confirmLabel: "Slett",
      tone: "danger",
    });
    if (!ok) return;

    const selectedDeleteExpressionId = expression.id;
    setIsSubmitting(true);
    try {
      await execute(
        `/api/v1/expressions/${selectedDeleteExpressionId}`,
        "DELETE",
      );

      setExpressions((prevExpressions) =>
        prevExpressions.filter((exp) => exp.id !== selectedDeleteExpressionId),
      );
      toast.success("Uttrykket er slettet.");
    } catch (err) {
      console.error("Feil ved sletting av uttrykk:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Duaer, navn og uttrykk"
        subtitle="Islamsk innhold i appen. Endringer vises med en gang."
      />
      <div className={styles.tabsRow}>
        <SectionTabs />
        <span className={styles.tabsAction}>
          <Button
            variant="secondary"
            size="sm"
            icon={<Icon name="plus" size={16} strokeWidth={2} />}
            onClick={() => setShowAddModal(true)}
          >
            Nytt uttrykk
          </Button>
        </span>
      </div>

      {!loaded && isLoading ? (
        <div className={styles.grid}>
          {[0, 1, 2, 3].map((i) => (
            <Card key={i} className={styles.item}>
              <Skeleton width="50%" height={18} />
              <Skeleton height={36} />
              <Skeleton width="80%" />
            </Card>
          ))}
        </div>
      ) : error && expressions.length === 0 ? (
        <Alert tone="error">
          Kunne ikke hente uttrykkene: {String(error.text)}
        </Alert>
      ) : expressions.length === 0 ? (
        <EmptyState
          icon="quote"
          title="Ingen uttrykk ennå"
          action={
            <Button
              variant="secondary"
              size="sm"
              icon={<Icon name="plus" size={16} strokeWidth={2} />}
              onClick={() => setShowAddModal(true)}
            >
              Nytt uttrykk
            </Button>
          }
        >
          Legg til det første uttrykket.
        </EmptyState>
      ) : (
        <div className={styles.grid}>
          {expressions.map((expression) => (
            <Card key={expression.id} className={styles.item}>
              <div className={styles.itemHead}>
                <b className={styles.itemTitle}>{expression.translit}</b>
                <Badge tone="grey">
                  <span className={ui.num}>#{expression.id}</span>
                </Badge>
                <IconButton
                  label="Rediger"
                  icon={<Icon name="edit" size={17} strokeWidth={1.9} />}
                  onClick={() => handleEditClick(expression)}
                />
                <IconButton
                  label="Slett"
                  icon={<Icon name="trash" size={17} strokeWidth={1.9} />}
                  onClick={() => handleDeleteClick(expression)}
                  disabled={isSubmitting}
                />
              </div>
              <p className={`${ui.arabic} ${styles.arabic}`} lang="ar">
                {expression.arabic}
              </p>
              <span className={styles.translation}>
                {expression.translation}
              </span>
              {expression.situation && (
                <span className={styles.situation}>
                  <b>Situasjon:</b> {expression.situation}
                </span>
              )}
            </Card>
          ))}
        </div>
      )}

      <ContentModal
        open={!!selectedExpression && showEditModal}
        title="Rediger uttrykk"
        icon="quote"
        width={560}
        saving={isSubmitting}
        saveLabel="Lagre"
        onSave={handleSave}
        onClose={() => setShowEditModal(false)}
      >
        {selectedExpression && (
          <ExpressionFieldsForm
            value={selectedExpression}
            onChange={setSelectedExpression}
          />
        )}
      </ContentModal>

      <ContentModal
        open={showAddModal}
        title="Nytt uttrykk"
        icon="plus"
        width={560}
        saving={isSubmitting}
        saveLabel="Legg til"
        onSave={handleAddNew}
        onClose={() => setShowAddModal(false)}
      >
        <ExpressionFieldsForm
          value={newExpression}
          onChange={setNewExpression}
        />
      </ContentModal>
    </>
  );
};

export default ExpressionsList;
